import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { expiryAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Download, FlaskConical } from 'lucide-react';
import { BUCKETS, ExpiryBadge } from './Batches';

const Expiry = () => {
    const { t } = useTranslation(['entities', 'common']);
    const [summary, setSummary] = useState({ counts: {}, units: {} });
    const [bucket, setBucket] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchSummary = async () => {
        try {
            const { data } = await expiryAPI.summary();
            setSummary(data);
        } catch {
            /* silent */
        }
    };

    const fetchList = async () => {
        setLoading(true);
        try {
            const { data } = await expiryAPI.list(bucket || undefined);
            setItems(data);
        } catch {
            toast.error(t('common:toasts.load_failed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSummary(); fetchList(); }, []);
    useEffect(() => { fetchList(); }, [bucket]);

    const exportCSV = () => {
        if (!items.length) { toast.error(t('entities:expiry.toasts.nothing_to_export')); return; }
        const headers = ['Batch', 'Product', 'SKU', 'Warehouse', 'Supplier', 'Mfg Date', 'Expiry', 'Days', 'Bucket', 'Remaining'];
        const esc = (v) => {
            if (v == null) return '';
            const s = String(v);
            return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = items.map((b) => [
            b.batch_number, b.product_name, b.sku, b.warehouse, b.supplier_name,
            b.manufacturing_date, b.expiry_date, b.days_to_expiry, b.expiry_bucket, b.quantity_remaining,
        ]);
        const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expiry_${bucket || 'urgent'}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(t('entities:expiry.toasts.exported', { count: items.length, defaultValue: `Exported ${items.length} batches` }));
    };

    return (
        <div data-testid="expiry-page" className="space-y-8 animate-fadeIn">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">{t('entities:expiry.section_label')}</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{t('entities:expiry.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('entities:expiry.subtitle')}</p>
                </div>
                <Button onClick={exportCSV} variant="outline" data-testid="export-expiry"
                        disabled={items.length === 0} className="border-[#E5E5E5]">
                    <Download size={14} className="mr-2" /> {t('common:actions.export_csv')}
                </Button>
            </div>

            {/* Bucket cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {BUCKETS.map((b) => {
                    const count = summary.counts?.[b.key] ?? 0;
                    const units = summary.units?.[b.key] ?? 0;
                    const active = bucket === b.key;
                    return (
                        <Card key={b.key}
                              data-testid={`bucket-card-${b.key}`}
                              onClick={() => setBucket(active ? '' : b.key)}
                              className={`cursor-pointer transition-all border-2 ${
                                  active ? 'border-[#C08478] shadow-md' : 'border-transparent shadow-sm hover:shadow-md'
                              }`}>
                            <CardContent className="p-4 space-y-2">
                                <Badge className={`${b.color} text-[10px] font-semibold`}>{t(`entities:batches.buckets.${b.key}`)}</Badge>
                                <p className="text-3xl font-bold text-[#1A1A1A]" data-testid={`bucket-count-${b.key}`}>{count}</p>
                                <p className="text-[10px] text-gray-500">{units.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {t('entities:expiry.units_suffix')}</p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <CalendarClock className="text-[#C08478]" size={20} />
                        {bucket
                            ? t('entities:expiry.list_title_filtered', { label: t(`entities:batches.buckets.${bucket}`) })
                            : t('entities:expiry.list_title_default')}
                        <span className="text-sm text-gray-400 font-normal">({items.length})</span>
                    </CardTitle>
                    {bucket && (
                        <Button variant="ghost" size="sm" onClick={() => setBucket('')} data-testid="clear-bucket"
                                className="text-xs text-[#C08478]">
                            {t('entities:expiry.clear_filter')}
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#C08478]" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-gray-400 flex flex-col items-center gap-3">
                            <FlaskConical size={36} className="opacity-40" />
                            <p>{t('entities:expiry.empty')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('entities:expiry.columns.batch')}</TableHead>
                                        <TableHead>{t('entities:expiry.columns.product')}</TableHead>
                                        <TableHead>{t('entities:expiry.columns.expiry')}</TableHead>
                                        <TableHead>{t('entities:expiry.columns.window')}</TableHead>
                                        <TableHead className="text-right">{t('entities:expiry.columns.remaining')}</TableHead>
                                        <TableHead>{t('entities:expiry.columns.warehouse')}</TableHead>
                                        <TableHead>{t('entities:expiry.columns.supplier')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.map((b) => (
                                        <TableRow key={b.id} data-testid={`expiry-row-${b.id}`}>
                                            <TableCell className="font-mono font-semibold text-sm">{b.batch_number}</TableCell>
                                            <TableCell>
                                                <p className="text-sm font-medium">{b.product_name}</p>
                                                <p className="text-[10px] text-gray-400 font-mono">{b.sku}</p>
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">{b.expiry_date}</TableCell>
                                            <TableCell><ExpiryBadge bucket={b.expiry_bucket} days={b.days_to_expiry} /></TableCell>
                                            <TableCell className="text-right font-mono font-semibold">{b.quantity_remaining}</TableCell>
                                            <TableCell className="text-sm">{b.warehouse || '-'}</TableCell>
                                            <TableCell className="text-sm">{b.supplier_name || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Expiry;
